import React from 'react';
import { usePageMeta } from '@/hooks/usePageMeta';
import './PrivacyPage.css';

export default function PrivacyPage() {
  usePageMeta(
    'Privacy Policy',
    'How The Gardeners Calendar collects, uses and protects your personal information.',
  );
  return (
    <div className="privacy-page">
      <div className="privacy-header">
        <div className="container-narrow">
          <p className="text-label" style={{ color: 'var(--sage)', marginBottom: 'var(--space-3)' }}>Legal</p>
          <h1 className="text-display">Privacy Policy</h1>
          <p className="text-subtitle" style={{ color: 'var(--ink-light)', marginTop: 'var(--space-3)' }}>
            How we collect, use, and protect your personal information.
          </p>
          <p className="text-caption" style={{ color: 'var(--ink-faint)', marginTop: 'var(--space-4)' }}>
            Last updated: March 2026
          </p>
        </div>
      </div>

      <div className="container-narrow privacy-body">
        <article className="paper prose privacy-article">

          <section>
            <h2>Who We Are</h2>
            <p>
              The Gardeners Calendar (<strong>the-gardeners-calendar.co.uk</strong>) is an organic and biodynamic gardening companion operated by White Pebble Projects. We are the data controller for personal information collected through this website.
            </p>
            <p>
              If you have any questions about this policy or how we handle your data, please contact us at <a href="mailto:hello@the-gardeners-calendar.co.uk">hello@the-gardeners-calendar.co.uk</a>.
            </p>
          </section>

          <section>
            <h2>What Information We Collect</h2>

            <h3>Account Information</h3>
            <p>
              When you sign in, we receive basic profile information from your chosen authentication provider. Depending on which provider you use, this may include:
            </p>
            <ul>
              <li>Your name or display name</li>
              <li>Your email address</li>
              <li>A unique identifier from the provider</li>
              <li>A profile picture (if available)</li>
            </ul>
            <p>We do not receive or store your passwords. Authentication is handled entirely by your chosen provider.</p>

            <h3>Garden Data</h3>
            <p>
              If you use the My Garden features, we store the information you choose to enter, including:
            </p>
            <ul>
              <li>Your garden details (name, location, size, soil type)</li>
              <li>Plants you add to your garden</li>
              <li>Diary entries and notes</li>
              <li>Planting and harvest records</li>
            </ul>
            <p>This data is associated with your account and stored securely on Microsoft Azure infrastructure.</p>

            <h3>Newsletter</h3>
            <p>
              If you sign up for our weekly newsletter, we store your email address together with a record of your consent — the date and time you ticked the box, where on the site you signed up, and a one-way hash of your IP address. We keep the hash rather than the address itself so we can investigate misuse of the form without retaining a record of where every subscriber was.
            </p>
            <p>
              We use this <strong>only</strong> to send you the newsletter. We do not sell, rent, or share subscriber addresses with anyone, and we will not use your address to send you anything you did not ask for. Every email includes a one-click unsubscribe link that works immediately and without signing in.
            </p>
            <p>
              Signing up for the newsletter does not require an account, and having an account does not sign you up for the newsletter. The two are entirely separate.
            </p>

            <h3>Usage Data</h3>
            <p>
              We may collect anonymised data about how the site is used — such as which pages are visited and how long is spent on them — to help us improve the service. This data cannot be used to identify you personally.
            </p>
          </section>

          <section>
            <h2>Authentication Providers</h2>
            <p>
              We use third-party authentication providers so you can sign in without creating a separate password. We currently support:
            </p>
            <ul>
              <li><strong>Google</strong> — governed by Google's Privacy Policy at <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></li>
              <li><strong>Facebook</strong> — governed by Meta's Privacy Policy at <a href="https://www.facebook.com/policy.php" target="_blank" rel="noopener noreferrer">facebook.com/policy.php</a></li>
              <li><strong>GitHub</strong> — governed by GitHub's Privacy Policy at <a href="https://docs.github.com/en/site-policy/privacy-policies/github-privacy-statement" target="_blank" rel="noopener noreferrer">docs.github.com</a></li>
              <li><strong>Microsoft</strong> — governed by Microsoft's Privacy Policy at <a href="https://privacy.microsoft.com" target="_blank" rel="noopener noreferrer">privacy.microsoft.com</a></li>
            </ul>
            <p>
              When you sign in via one of these providers, you are subject to their privacy policies as well as ours. We only request the minimum information needed to create and manage your account.
            </p>
          </section>

          <section>
            <h2>Cookies and Advertising</h2>

            <h3>Essential Cookies</h3>
            <p>
              We use session cookies to keep you signed in while you use the site. These are necessary for the service to function and cannot be disabled.
            </p>

            <h3>Google AdSense</h3>
            <p>
              This site displays advertisements served by Google AdSense. Google uses cookies to serve ads based on your prior visits to this and other websites. You can opt out of personalised advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google's Ad Settings</a> or <a href="https://www.aboutads.info" target="_blank" rel="noopener noreferrer">aboutads.info</a>.
            </p>
            <p>
              Google's use of advertising cookies is governed by their Privacy Policy. The ads you see may be personalised based on your browsing history, or non-personalised if you have opted out.
            </p>
            <p>
              For more information about how Google uses data when you use our site, visit <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">How Google uses information from sites or apps that use our services</a>.
            </p>
          </section>

          <section>
            <h2>How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide and maintain your account and garden journal</li>
              <li>Enable you to save plants, diary entries, and garden records</li>
              <li>Improve and develop the site and its features</li>
              <li>Respond to your enquiries and support requests</li>
              <li>Comply with our legal obligations</li>
            </ul>
            <p>
              We do not sell your personal data to third parties. We do not use your garden data or account information for advertising purposes.
            </p>
          </section>

          <section>
            <h2>Data Storage and Security</h2>
            <p>
              Your data is stored on Microsoft Azure infrastructure, hosted in the United Kingdom or European Economic Area. Azure maintains extensive security certifications and practices. For more information, see <a href="https://azure.microsoft.com/en-gb/explore/trusted-cloud" target="_blank" rel="noopener noreferrer">Microsoft's Trusted Cloud</a> documentation.
            </p>
            <p>
              We take reasonable technical and organisational measures to protect your personal data against unauthorised access, loss, or misuse. However, no method of transmission over the internet is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2>Data Retention</h2>
            <p>
              We retain your account and garden data for as long as your account is active. If you request deletion of your account, we will remove your personal data within 30 days, except where we are required to retain it by law.
            </p>
            <p>
              Newsletter subscriptions are kept until you unsubscribe. When you unsubscribe we keep a minimal record — your address and the fact that you opted out — so that we do not accidentally add you back to the list later. If you would rather we erased that record entirely, ask us and we will.
            </p>
          </section>

          <section>
            <h2>Your Rights</h2>
            <p>Under UK GDPR and the Data Protection Act 2018, you have the right to:</p>
            <ul>
              <li><strong>Access</strong> — request a copy of the personal data we hold about you</li>
              <li><strong>Rectification</strong> — ask us to correct inaccurate or incomplete data</li>
              <li><strong>Erasure</strong> — ask us to delete your personal data ("right to be forgotten")</li>
              <li><strong>Restriction</strong> — ask us to restrict how we process your data</li>
              <li><strong>Portability</strong> — receive your data in a structured, machine-readable format</li>
              <li><strong>Objection</strong> — object to processing of your data in certain circumstances</li>
              <li><strong>Withdrawal of consent</strong> — where we rely on your consent, such as the newsletter, you can withdraw it at any time. The unsubscribe link in every email does this instantly.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at <a href="mailto:hello@the-gardeners-calendar.co.uk">hello@the-gardeners-calendar.co.uk</a>. We will respond within 30 days.
            </p>
            <p>
              You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a> if you believe we have handled your data unlawfully.
            </p>
          </section>

          <section>
            <h2>Children's Privacy</h2>
            <p>
              The Gardeners Calendar is not directed at children under the age of 13. We do not knowingly collect personal data from children. If you believe a child has provided us with personal information, please contact us and we will delete it promptly.
            </p>
          </section>

          <section>
            <h2>Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. We will note the date of the most recent revision at the top of this page. Continued use of the site after changes are posted constitutes your acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2>Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this privacy policy or your personal data, please contact us:
            </p>
            <p>
              <strong>Email:</strong> <a href="mailto:hello@the-gardeners-calendar.co.uk">hello@the-gardeners-calendar.co.uk</a><br />
              <strong>Website:</strong> <a href="https://www.the-gardeners-calendar.co.uk">the-gardeners-calendar.co.uk</a>
            </p>
          </section>

        </article>
      </div>
    </div>
  );
}
